import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Paper,
  Snackbar
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  FiberManualRecord as DotIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import SelectorCampos from '../../components/CamposDinamicos/SelectorCampos';
import SelectorAcciones from '../../components/CamposDinamicos/SelectorAcciones';
import empresasApi from '../../api/empresasApi';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL;

const ConfiguracionCamposEmpresas = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Estados
  const [empresas, setEmpresas] = useState([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState('');
  const [estadosEmpresa, setEstadosEmpresa] = useState([]);
  const [estadoActual, setEstadoActual] = useState(0);
  const [catalogoCampos, setCatalogoCampos] = useState([]);
  const [configuracionPorEstado, setConfiguracionPorEstado] = useState({});
  const [accionesPorEstado, setAccionesPorEstado] = useState({});
  const [cambiosPendientes, setCambiosPendientes] = useState({});
  const [cambiosAccionesPendientes, setCambiosAccionesPendientes] = useState({});

  useEffect(() => {
    cargarEmpresas();
    cargarCatalogoCampos();
  }, []);

  useEffect(() => {
    if (empresaSeleccionada) {
      cargarEstadosEmpresa();
      cargarConfiguracionEmpresa();
    }
  }, [empresaSeleccionada]);

  const cargarEmpresas = async () => {
    try {
      const data = await empresasApi.listar();
      console.log('📊 Datos recibidos de empresasApi.listar():', data);

      // La API devuelve { success: true, data: [...] }
      let empresasData = [];

      if (Array.isArray(data)) {
        empresasData = data;
      } else if (data && Array.isArray(data.data)) {
        empresasData = data.data;
      } else if (data && Array.isArray(data.empresas)) {
        empresasData = data.empresas;
      }

      // Mapear los campos porque la API usa 'id' y 'nombre' pero necesitamos 'id_empresa' y 'nombre_empresa'
      empresasData = empresasData.map(emp => ({
        id_empresa: emp.id_empresa || emp.id,
        nombre_empresa: emp.nombre_empresa || emp.nombre,
        ...emp
      }));

      console.log('✅ Empresas procesadas:', empresasData);
      setEmpresas(empresasData);
    } catch (err) {
      console.error('❌ Error cargando empresas:', err);
      setError('Error al cargar empresas');
      setEmpresas([]);
    }
  };

  const cargarCatalogoCampos = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/v1/catalogo-campos`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );
      const catalogoData = Array.isArray(response.data) ? response.data : [];
      setCatalogoCampos(catalogoData);
    } catch (err) {
      console.error('Error cargando catálogo:', err);
      setError('Error al cargar catálogo de campos');
      setCatalogoCampos([]); // Asegurar que siempre sea un array
    }
  };

  const cargarEstadosEmpresa = async () => {
    try {
      // Usar el endpoint de estados-activos con id_empresa como query param
      const response = await axios.get(
        `${API_URL}/api/v1/configuracion/estados-activos?id_empresa=${empresaSeleccionada}`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );

      console.log('📊 Estados recibidos:', response.data);

      // La API devuelve { success: true, data: [...estados...] }
      const estadosActivos = response.data.data || response.data || [];

      // Mapear los campos para asegurar compatibilidad
      const estadosMapeados = estadosActivos.map(estado => ({
        id_estado: estado.id_estado || estado.id,
        nombre_estado: estado.nombre_estado || estado.nombre,
        color: estado.color || '#ccc',
        ...estado
      }));

      console.log('✅ Estados procesados:', estadosMapeados);
      setEstadosEmpresa(estadosMapeados);

      if (estadosMapeados.length > 0) {
        setEstadoActual(0);
      }
    } catch (err) {
      console.error('❌ Error cargando estados:', err);
      setError('Error al cargar estados de la empresa');
      setEstadosEmpresa([]);
    }
  };

  const cargarConfiguracionEmpresa = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${API_URL}/api/v1/empresa/${empresaSeleccionada}/campos-estados`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );

      console.log('📊 Configuración recibida:', response.data);

      // La API devuelve un array de estados, cada uno con su array de campos
      // Formato: [{ id_estado_servicio, nombre_estado, color_estado, campos: [...] }]
      let estadosConCampos = [];
      if (Array.isArray(response.data)) {
        estadosConCampos = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        estadosConCampos = response.data.data;
      }

      console.log('📋 Estados con campos:', estadosConCampos);

      // Organizar por estado
      const configuracion = {};
      estadosConCampos.forEach(estado => {
        const estadoId = estado.id_estado_servicio || estado.id_estado;
        const camposDelEstado = estado.campos || [];

        configuracion[estadoId] = camposDelEstado.map(campo => ({
          id_campo: campo.id_campo,
          nombre_campo: campo.nombre_campo,
          etiqueta_display: campo.etiqueta_display,
          tipo_campo: campo.tipo_campo,
          es_obligatorio: campo.es_obligatorio,
          orden_visualizacion: campo.orden_visualizacion,
          id_asignacion: campo.id_asignacion,
          ...campo
        }));
      });

      console.log('✅ Configuración por estado:', configuracion);
      setConfiguracionPorEstado(configuracion);
      setCambiosPendientes({});

      // Cargar acciones para cada estado
      await cargarAccionesEmpresa();
    } catch (err) {
      console.error('❌ Error cargando configuración:', err);
      setError('Error al cargar la configuración');
      setConfiguracionPorEstado({});
    } finally {
      setLoading(false);
    }
  };

  const cargarAccionesEmpresa = async () => {
    try {
      // Cargar acciones de cada estado
      const accionesConfig = {};

      for (const estado of estadosEmpresa) {
        try {
          const response = await axios.get(
            `${API_URL}/api/v1/empresa/${empresaSeleccionada}/estado/${estado.id_estado}/acciones`,
            {
              headers: { Authorization: `Bearer ${user.token}` }
            }
          );

          const accionesData = response.data.data || response.data || [];
          accionesConfig[estado.id_estado] = Array.isArray(accionesData) ? accionesData : [];
        } catch (err) {
          // Si es 404, significa que no hay acciones configuradas para este estado
          if (err.response?.status === 404) {
            accionesConfig[estado.id_estado] = [];
          } else {
            console.error(`Error cargando acciones del estado ${estado.id_estado}:`, err);
            accionesConfig[estado.id_estado] = [];
          }
        }
      }

      console.log('✅ Acciones por estado:', accionesConfig);
      setAccionesPorEstado(accionesConfig);
      setCambiosAccionesPendientes({});
    } catch (err) {
      console.error('❌ Error cargando acciones:', err);
    }
  };

  const handleCambioEmpresa = (e) => {
    setEmpresaSeleccionada(e.target.value);
    setEstadoActual(0);
    setConfiguracionPorEstado({});
    setCambiosPendientes({});
  };

  const handleCamposChange = (nuevoCampos) => {
    const estadoId = estadosEmpresa[estadoActual].id_estado;

    setConfiguracionPorEstado({
      ...configuracionPorEstado,
      [estadoId]: nuevoCampos
    });

    setCambiosPendientes({
      ...cambiosPendientes,
      [estadoId]: true
    });
  };

  const handleAccionesChange = (nuevasAcciones) => {
    const estadoId = estadosEmpresa[estadoActual].id_estado;

    setAccionesPorEstado({
      ...accionesPorEstado,
      [estadoId]: nuevasAcciones
    });

    setCambiosAccionesPendientes({
      ...cambiosAccionesPendientes,
      [estadoId]: true
    });
  };

  const handleGuardarCambios = async () => {
    try {
      setLoading(true);
      setError(null);

      // Guardar campos con cambios pendientes
      const estadosConCambiosCampos = Object.keys(cambiosPendientes).filter(
        estadoId => cambiosPendientes[estadoId]
      );

      for (const estadoId of estadosConCambiosCampos) {
        const campos = configuracionPorEstado[estadoId] || [];

        await axios.post(
          `${API_URL}/api/v1/empresa/${empresaSeleccionada}/estado/${estadoId}/campos`,
          { campos },
          {
            headers: { Authorization: `Bearer ${user.token}` }
          }
        );
      }

      // Guardar acciones con cambios pendientes
      const estadosConCambiosAcciones = Object.keys(cambiosAccionesPendientes).filter(
        estadoId => cambiosAccionesPendientes[estadoId]
      );

      for (const estadoId of estadosConCambiosAcciones) {
        const acciones = accionesPorEstado[estadoId] || [];

        await axios.post(
          `${API_URL}/api/v1/empresa/${empresaSeleccionada}/estado/${estadoId}/acciones`,
          { acciones },
          {
            headers: { Authorization: `Bearer ${user.token}` }
          }
        );
      }

      setCambiosPendientes({});
      setCambiosAccionesPendientes({});
      setSuccess(true);

      // Recargar configuración
      cargarConfiguracionEmpresa();
    } catch (err) {
      console.error('Error guardando cambios:', err);
      setError('Error al guardar los cambios');
    } finally {
      setLoading(false);
    }
  };

  const handleRecargar = () => {
    cargarConfiguracionEmpresa();
  };

  const hayCambiosPendientes = Object.values(cambiosPendientes).some(v => v) || Object.values(cambiosAccionesPendientes).some(v => v);

  const estadoSeleccionado = estadosEmpresa[estadoActual];
  const camposDelEstado = estadoSeleccionado
    ? configuracionPorEstado[estadoSeleccionado.id_estado] || []
    : [];
  const accionesDelEstado = estadoSeleccionado
    ? accionesPorEstado[estadoSeleccionado.id_estado] || []
    : [];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink color="inherit" href="/admin">
          Admin
        </MuiLink>
        <Typography color="text.primary">Configuración de Campos por Empresa</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SettingsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Configuración de Campos por Empresa
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Asigna campos del catálogo a los estados de cada empresa
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRecargar}
            disabled={loading}
          >
            Recargar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleGuardarCambios}
            disabled={!hayCambiosPendientes || loading}
            color={hayCambiosPendientes ? 'warning' : 'primary'}
          >
            Guardar Cambios {hayCambiosPendientes && `(${Object.values(cambiosPendientes).filter(v => v).length})`}
          </Button>
        </Box>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {hayCambiosPendientes && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Tienes cambios sin guardar. No olvides hacer clic en "Guardar Cambios".
        </Alert>
      )}

      {/* Selector de Empresa */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {empresas.length === 0 ? (
            <Alert severity="info">
              No hay empresas registradas en el sistema. Crea una empresa primero.
            </Alert>
          ) : (
            <FormControl fullWidth>
              <InputLabel>Seleccionar Empresa</InputLabel>
              <Select
                value={empresaSeleccionada}
                onChange={handleCambioEmpresa}
                label="Seleccionar Empresa"
              >
                {empresas.map((empresa) => (
                  <MenuItem key={empresa.id_empresa} value={empresa.id_empresa}>
                    {empresa.nombre_empresa}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </CardContent>
      </Card>

      {/* Configuración por Estado */}
      {empresaSeleccionada && (
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : estadosEmpresa.length === 0 ? (
              <Alert severity="info">
                Esta empresa no tiene estados activos configurados.
              </Alert>
            ) : (
              <>
                {/* Tabs de Estados */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs
                    value={estadoActual}
                    onChange={(e, newValue) => setEstadoActual(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {estadosEmpresa.map((estado, index) => (
                      <Tab
                        key={estado.id_estado}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{estado.nombre_estado}</span>
                            {(cambiosPendientes[estado.id_estado] || cambiosAccionesPendientes[estado.id_estado]) && (
                              <DotIcon sx={{ fontSize: 12, color: 'warning.main' }} />
                            )}
                          </Box>
                        }
                        sx={{
                          borderLeft: `4px solid ${estado.color || '#ccc'}`,
                          fontWeight: estadoActual === index ? 'bold' : 'normal'
                        }}
                      />
                    ))}
                  </Tabs>
                </Box>

                {/* Panel de Configuración */}
                {estadoSeleccionado && (
                  <Box>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Estado: {estadoSeleccionado.nombre_estado}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Configura qué acciones (botones) y campos aparecerán cuando una orden esté en este estado.
                        Puedes ordenarlos y marcar cuáles campos son obligatorios.
                      </Typography>
                    </Paper>

                    {/* Selector de Acciones */}
                    <Box sx={{ mb: 4 }}>
                      <SelectorAcciones
                        accionesAsignadas={accionesDelEstado}
                        onAccionesChange={handleAccionesChange}
                        readOnly={false}
                      />
                    </Box>

                    {/* Selector de Campos */}
                    <SelectorCampos
                      camposAsignados={camposDelEstado}
                      catalogoCampos={catalogoCampos}
                      onCamposChange={handleCamposChange}
                      readOnly={false}
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Snackbar de éxito */}
      <Snackbar
        open={success}
        autoHideDuration={4000}
        onClose={() => setSuccess(false)}
        message="Cambios guardados exitosamente"
      />
    </Container>
  );
};

export default ConfiguracionCamposEmpresas;
